// UploadMedia.js
import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    collection, addDoc, updateDoc, deleteDoc, onSnapshot, doc,
    serverTimestamp, Timestamp
} from 'firebase/firestore';
import { storage, db, auth } from '../firebaseConfig';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../AuthProvider';
import '../styles/BellaBizarreStyle.css';

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

const UploadMedia = () => {
    const { user } = useAuth();

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [mediaType, setMediaType] = useState('image');
    const [file, setFile] = useState(null);
    const [embedUrl, setEmbedUrl] = useState('');
    const [url, setUrl] = useState('');

    // NEW: optional fields
    const [categoriesInput, setCategoriesInput] = useState(''); // "cat1, cat2"
    const [dateInput, setDateInput] = useState(''); // "YYYY-MM-DD"

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [items, setItems] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const fileRef = useRef(null);

    useEffect(() => {
        if (!user) return;
        const unsub = onSnapshot(
            collection(db, 'media'),
            snap => setItems(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
            err => console.error(err)
        );
        return unsub;
    }, [user]);

    if (!user) {
        return (
            <div className="event-uploader-container">
                <p>Please <Link to="/login">log in</Link> to manage media.</p>
            </div>
        );
    }

    const resetForm = () => {
        setName('');
        setDescription('');
        setMediaType('image');
        setFile(null);
        setEmbedUrl('');
        setUrl('');
        setCategoriesInput('');
        setDateInput('');
        setEditingId(null);
        setMessage('');
        if (fileRef.current) fileRef.current.value = '';
    };

    const handleFileChange = e => {
        const selected = e.target.files?.[0];
        if (selected && selected.size > MAX_FILE_SIZE) {
            setMessage('File exceeds 100MB');
            fileRef.current.value = '';
            return;
        }
        setFile(selected);
        setMessage('');
    };

    const handleEdit = item => {
        setEditingId(item.id);
        setName(item.name || '');
        setDescription(item.description || '');
        setMediaType(item.mediaType || 'image');

        if (item.mediaType === 'embed') {
            setEmbedUrl(item.url || '');
            setUrl(item.url || '');
        } else {
            setUrl(item.url || '');
        }

        // Populate optional fields
        setCategoriesInput(Array.isArray(item.categories) ? item.categories.join(', ') : '');
        if (item.date) {
            const d = item.date.toDate ? item.date.toDate() : new Date(item.date);
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            setDateInput(`${yyyy}-${mm}-${dd}`);
        } else {
            setDateInput('');
        }

        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async id => {
        if (!window.confirm('Delete this media?')) return;
        try {
            await deleteDoc(doc(db, 'media', id));
            setMessage('Media deleted');
        } catch (err) {
            console.error(err);
            setMessage('Delete failed');
        }
    };

    const handleSubmit = async e => {
        e.preventDefault();
        setLoading(true);
        setMessage('');
        let finalUrl = '';

        try {
            if (mediaType === 'embed') {
                finalUrl = embedUrl.trim();
                if (!finalUrl) throw new Error('Invalid embed URL');
            } else if (file) {
                const path = `uploads/media/${Date.now()}_${file.name}`;
                const storageRef = ref(storage, path);
                const task = uploadBytesResumable(storageRef, file, { contentType: file.type });
                await new Promise((res, rej) => {
                    task.on('state_changed', null, rej, async () => {
                        finalUrl = await getDownloadURL(task.snapshot.ref);
                        res();
                    });
                });
            } else {
                throw new Error('Select file or embed link');
            }

            // Prepare optional fields
            const categories = categoriesInput
                .split(',')
                .map(s => s.trim())
                .filter(Boolean);

            let dateField = null;
            if (dateInput) {
                const d = new Date(dateInput + 'T00:00:00');
                if (!isNaN(d.getTime())) {
                    dateField = Timestamp.fromDate(d);
                }
            }

            const data = {
                name,
                description,
                mediaType,
                url: finalUrl,
                createdAt: serverTimestamp(),
                // NEW fields
                categories: categories.length ? categories : [],
                date: dateField || null,
                // (Optional) If you want owner filtering in Gallery delete button:
                ownerId: user?.uid || null,
            };

            if (editingId) {
                await updateDoc(doc(db, 'media', editingId), data);
                setMessage('Media updated');
            } else {
                await addDoc(collection(db, 'media'), data);
                setMessage('Media uploaded');
            }

            resetForm();
        } catch (err) {
            console.error(err);
            setMessage(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="event-uploader-container">
            <h2>{editingId ? 'Edit Media' : 'Upload New Media'}</h2>
            <form onSubmit={handleSubmit} className="event-uploader-form">
                <label>
                    Name:
                    <input type="text" value={name} onChange={e => setName(e.target.value)} required />
                </label>

                <label>
                    Description:
                    <textarea value={description} onChange={e => setDescription(e.target.value)} required />
                </label>

                <label>
                    Type:
                    <select value={mediaType} onChange={e => setMediaType(e.target.value)}>
                        <option value="image">Image</option>
                        <option value="video">Video</option>
                        <option value="audio">Audio</option>
                        <option value="embed">Embed Link</option>
                    </select>
                </label>

                {/* NEW: Categories (optional) */}
                <label>
                    Categories (optional, comma-separated):
                    <input
                        type="text"
                        placeholder="posters, promo, behind-the-scenes"
                        value={categoriesInput}
                        onChange={e => setCategoriesInput(e.target.value)}
                    />
                </label>

                {/* NEW: Date (optional) */}
                <label>
                    Date (optional):
                    <input
                        type="date"
                        value={dateInput}
                        onChange={e => setDateInput(e.target.value)}
                    />
                </label>

                {mediaType === 'embed' ? (
                    <label>
                        Embed URL:
                        <input type="url" value={embedUrl} onChange={e => setEmbedUrl(e.target.value)} required />
                    </label>
                ) : (
                    <label>
                        Select File:
                        <input type="file" accept={`${mediaType}/*`} onChange={handleFileChange} ref={fileRef} />
                    </label>
                )}

                {message && <p className="event-uploader-message">{message}</p>}

                <button type="submit" disabled={loading} className="event-uploader-submit-button">
                    {loading ? (editingId ? 'Updating...' : 'Uploading...') : (editingId ? 'Update' : 'Upload')}
                </button>

                {editingId && (
                    <button type="button" onClick={resetForm} className="event-uploader-reset-button">
                        Cancel
                    </button>
                )}
            </form>

            <h2 className="event-uploader-current-title">Current Media</h2>
            {items.length > 0 ? (
                <div className="event-uploader-preview-container">
                    {items.map(item => (
                        <div key={item.id} className="event-uploader-event-card">
                            {item.mediaType === 'image' && <img src={item.url} alt={item.name} className="gallery-image" />}
                            {item.mediaType === 'video' && <video controls src={item.url} />}
                            {item.mediaType === 'audio' && <audio controls src={item.url} />}
                            {item.mediaType === 'embed' && <iframe src={item.url} title={item.name} frameBorder="0" />}

                            <div className="event-uploader-event-content">
                                <h3>{item.name}</h3>
                                <p>{item.description}</p>

                                {/* Show categories/date if present */}
                                {Array.isArray(item.categories) && item.categories.length > 0 && (
                                    <p><strong>Categories:</strong> {item.categories.join(', ')}</p>
                                )}
                                {item.date && (
                                    <p><strong>Date:</strong> {item.date.toDate ? item.date.toDate().toLocaleDateString() : String(item.date)}</p>
                                )}

                                <p><em>{item.createdAt?.toDate ? item.createdAt.toDate().toLocaleString() : ''}</em></p>

                                <div className="event-uploader-card-buttons">
                                    <button onClick={() => handleEdit(item)} className="event-uploader-edit-button">Edit</button>
                                    <button onClick={() => handleDelete(item.id)} className="event-uploader-delete-button">Delete</button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <p>No media uploaded yet.</p>
            )}
        </div>
    );
};

export default UploadMedia;
